import React, { useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import useDatatable, {
  addRingCentralContactToStore,
  buildRowActionsRendererDefinition,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import { rowClickBuilder } from 'hooks/profileActions';
import { useExtendedParams } from 'hooks/useExtendedParams';
import { UserProviderContext } from 'providers/UserProvider';
import strings from 'strings';
import DataTable from 'UI/components/organisms/DataTable';
import { CommunicationActionPreset } from 'UI/constants/defaults';
import { TabKeys } from 'UI/constants/entityTypes';
import { getProfileQuickViewByEntity } from 'UI/constants/LogRocketCustomEvents';
import { getFinalColumns } from 'UI/utils';

import { getEmployeeRole } from '../EmployeesTab/columns';

import ProfileStructure from './components';

/** @typedef {import('types/app').ProfileDataTableProps} ProfileDataTableProps */

/**
 * @typedef {Object} OrderByOptionsTypes
 * @property {string} column
 * @property {string} direction
 */

const {
  inventoryProfiles: { emptyStates }
} = strings;

const tableShadowDeep = 2;

/**
 * @param {Object} props
 * @param {Object} props.actionButtonProps
 * @param {string} props.actionText
 * @param {Function} [props.customRowPermissionResolver]
 * @param {ProfileDataTableProps} props.DataTableProps
 * @param {Array<Object>} props.extraRenderers
 * @param {Array<Object>} props.finalActions
 * @param {Function} [props.customColumnsExtender]
 * @param {boolean} props.hasProfileLoaded
 * @param {ReactNode} props.infoLabel
 * @param {Array<Object>} props.initialColumns
 * @param {boolean} props.isQuickViewMode
 * @param {Function} props.onNewItemClick
 * @param {Function} props.onQuickViewClick
 * @param {OrderByOptionsTypes} props.orderByOptions
 * @param {string} props.profileModule
 * @param {boolean} props.shouldRefresh
 * @param {string} props.tabKey
 * @param {Array<Object>} props.paramsBuilder
 */
const ProfileTableLayout = ({
  actionButtonProps,
  actionText,
  customActionsDefinitions = null,
  customColumnsExtender,
  DataTableProps,
  extraRenderers,
  finalActions,
  hasProfileLoaded,
  infoLabel,
  initialColumns,
  isQuickViewMode,
  onNewItemClick,
  onQuickViewClick,
  orderByOptions,
  paramsBuilder,
  profileModule,
  shouldRefresh,
  tabKey,
  tablePreferencesKey,
  ...rest
}) => {
  const dispatch = useDispatch();

  const [user] = useContext(UserProviderContext);

  const columnsFromTab = customColumnsExtender ? customColumnsExtender(handleAction) : [];

  const PROFILE_TABLE_COLUMNS = [...getFinalColumns(initialColumns), ...columnsFromTab].filter(
    column => !!column
  );

  const initialPreferences = getColumnPreferences(
    tablePreferencesKey,
    0,
    orderByOptions,
    PROFILE_TABLE_COLUMNS
  );

  const { extendParams } = useExtendedParams(paramsBuilder, null);

  const {
    columnPreferences,
    count,
    data: tabData,
    getData,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersToggle,
    handleKeywordChange,
    handlePageChange,
    handlePerPageChange,
    listState
  } = useDatatable({
    initialPreferences,
    columnsDefinitions: PROFILE_TABLE_COLUMNS,
    key: tablePreferencesKey,
    paramsExtender: extendParams,
    shouldUseSavedPagination: false,
    ...DataTableProps,
    shouldScrollOnNavigate: true
  });

  const { columns, columnOrder, orderBy, direction } = columnPreferences;
  const { isLoading, showWholeSkeleton, page, perPage, keyword } = listState;

  const customActionButtonDefinitions = useMemo(
    () =>
      customActionsDefinitions?.map(item => ({
        ...item,
        props: {
          ...item.props,
          onClick: () =>
            item?.props?.onClick &&
            item.props.onClick({
              tabData,
              getData
            })
        }
      })),
    [customActionsDefinitions, getData, tabData]
  );

  function handleAction({ rowData, origin }) {
    const role =
      tabKey === TabKeys.Employees ? getEmployeeRole({ columns, rowData }) : profileModule;
    addRingCentralContactToStore({
      rowData,
      columns: PROFILE_TABLE_COLUMNS,
      dispatch,
      role,
      origin
    });
  }
  const handleQuickViewClick = itemData => onQuickViewClick(itemData, profileModule, tabData);

  const quickView = {
    name: 'view',
    callback: handleQuickViewClick,
    enableTracking: true,
    logRocketEvent: getProfileQuickViewByEntity(profileModule)
  };

  const actionsDefinition = onQuickViewClick ? [...finalActions, quickView] : finalActions;

  const actions = actionsDefinition.map(item => {
    const { name = '', callback = () => {}, idKey = null, pickId = false, isVisible = true } = item;

    const presetNames = CommunicationActionPreset.map(preset => preset.name);
    if (presetNames.includes(name)) return item;

    const enableWhen = (() => {
      // this is for enable/disable icon button
      if (item.checkIfShouldBeEnabled) {
        return data => item.checkIfShouldBeEnabled(data, tabData, initialColumns, user);
      }

      return () => true;
    })();

    return isVisible
      ? {
          name,
          onClick: rowClickBuilder({
            callback,
            columns,
            data: tabData,
            getData,
            idKey: idKey || 'id',
            pickId,
            enableTracking: item?.enableTracking,
            logRocketEvent: item?.logRocketEvent
          }),
          enableWhen
        }
      : {};
  });

  const filteredActions = actions.filter(obj => Object.keys(obj).length > 0);

  const profileEditionActions = buildRowActionsRendererDefinition({
    actions: filteredActions,
    namespace: profileModule,
    handleAction,
    columns: PROFILE_TABLE_COLUMNS
  });

  const tableActions = actions.length !== 0 ? profileEditionActions : null;

  const finalColumns = getColumnsToRender([...columns, tableActions], extraRenderers);

  useEffect(() => {
    shouldRefresh && getData();
  }, [shouldRefresh, getData]);

  const emptyTableStrings = emptyStates[tabKey];
  const emptyStateStringsFallBack = section => emptyTableStrings?.[section] ?? emptyStates.fallBack;

  const emptyTableStringsModes = emptyStateStringsFallBack('profile');

  return (
    <ProfileStructure
      isLoading={isLoading}
      TableRecordsProps={{ count, infoLabel, isQuickViewMode }}
      TableActionsProps={{
        actionButtonProps,
        actionText,
        customActionsDefinitions: customActionButtonDefinitions,
        hasProfileLoaded,
        onNewItemClick
      }}
      profileTable={
        <DataTable
          columnOrder={columnOrder?.length && columnOrder}
          columns={finalColumns}
          count={count}
          data={tabData}
          download={false}
          elevation={tableShadowDeep}
          filter={false}
          isExpandable
          loading={showWholeSkeleton}
          onColumnDisplayClick={handleColumnDisplayChange}
          onColumnOrderChange={handleColumnOrderChange}
          onColumnSortChange={handleColumnSortChange}
          onPageClick={handlePageChange}
          onPerPageClick={handlePerPageChange}
          onSearchTextChange={handleKeywordChange}
          onToggleFilters={handleFiltersToggle}
          page={page}
          print={false}
          refreshing={isLoading}
          responsive="standard"
          rowsPerPage={perPage}
          searchText={keyword}
          selectableRows="none"
          selectToolbarPlacement="none"
          sortOrder={{ name: orderBy, direction }}
          draggableColumns={{
            enabled: true
          }}
          {...emptyTableStringsModes}
          {...rest}
        />
      }
    />
  );
};

ProfileTableLayout.defaultProps = {
  finalActions: [],
  paramsBuilder: [],
  orderByOptions: {
    column: 'id',
    direction: 'desc'
  },
  customRowPermissionResolver: undefined,
  shouldRefresh: false,
  hasProfileLoaded: false,
  profileModule: '',
  onQuickViewClick: null
};

export default ProfileTableLayout;
